import { useEffect, useRef, useState } from "react";
import { type TimeReportsV2Preferences } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";

interface UseTimeReportsPreferencesSyncProps {
  companyId: number | undefined;
  preferences: TimeReportsV2Preferences;
  loadTimeReportsV2Preferences: (companyId: number) => void;
  persistTimeReportsV2Preferences: (
    companyId: number,
    preferences: TimeReportsV2Preferences,
  ) => void;
}

/** Syncs time reports preferences by loading on company change and persisting later updates. */
export const useTimeReportsPreferencesSync = (
  props: UseTimeReportsPreferencesSyncProps,
): boolean => {
  const {
    companyId,
    preferences,
    loadTimeReportsV2Preferences,
    persistTimeReportsV2Preferences,
  } = props;
  const hydratedPreferencesCompanyIdRef = useRef<number | null>(null);
  const skipInitialPersistCompanyIdRef = useRef<number | null>(null);
  const [isPreferencesHydrated, setIsPreferencesHydrated] = useState(false);

  useEffect(() => {
    if (companyId == null) {
      hydratedPreferencesCompanyIdRef.current = null;
      skipInitialPersistCompanyIdRef.current = null;
      setIsPreferencesHydrated(false);
      return;
    }

    setIsPreferencesHydrated(false);
    hydratedPreferencesCompanyIdRef.current = null;
    skipInitialPersistCompanyIdRef.current = companyId;
    loadTimeReportsV2Preferences(companyId);
    hydratedPreferencesCompanyIdRef.current = companyId;
    setIsPreferencesHydrated(true);
  }, [companyId, loadTimeReportsV2Preferences]);

  useEffect(() => {
    if (companyId == null) return;
    if (hydratedPreferencesCompanyIdRef.current !== companyId) return;
    if (skipInitialPersistCompanyIdRef.current === companyId) {
      skipInitialPersistCompanyIdRef.current = null;
      return;
    }

    persistTimeReportsV2Preferences(companyId, preferences);
  }, [companyId, persistTimeReportsV2Preferences, preferences]);

  return isPreferencesHydrated;
};
